// AI туслахын файлууд — хэрэглэгчийн хөтөч дээр үүснэ (серверт ачаалал, зардалгүй):
// Word (.doc — Word, Google Docs нээнэ), PDF (хэвлэх цонх → "Save as PDF"), PowerPoint (pptxgenjs), Excel, CSV, Markdown, текст, HTML.
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PURPLE = "5C1090";
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const safeName = (s) => String(s || "AI файл").replace(/[\\/:*?"<>|]/g, " ").trim() || "AI файл";
// Слайд, хүснэгтийн нүдэнд markdown тэмдэгтгүй текст
const plain = (s) => String(s ?? "").replace(/\*\*(.+?)\*\*/g, "$1").replace(/(^|\s)\*(.+?)\*(?=\s|$)/g, "$1$2").replace(/`([^`]+)`/g, "$1").trim();

export const mdToHtml = (md) => renderToStaticMarkup(<ReactMarkdown remarkPlugins={[remarkGfm]}>{md || ""}</ReactMarkdown>);

const STYLE = `
  body { font-family: Arial, "Helvetica Neue", sans-serif; font-size: 11pt; line-height: 1.45; color: #1c1c1c; }
  h1 { font-size: 20pt; color: #${PURPLE}; margin: 0 0 8pt; } h2 { font-size: 15pt; color: #${PURPLE}; margin: 14pt 0 6pt; } h3 { font-size: 12.5pt; margin: 12pt 0 4pt; }
  p { margin: 4pt 0; } ul, ol { margin: 4pt 0 4pt 18pt; padding: 0; }
  table { border-collapse: collapse; width: 100%; margin: 6pt 0; } th, td { border: 1px solid #999; padding: 4pt 6pt; vertical-align: top; text-align: left; }
  th { background: #f3eeff; } blockquote { margin: 6pt 0; padding: 4pt 10pt; border-left: 3pt solid #e5d8f5; color: #444; }
  code { font-family: Consolas, monospace; font-size: 10pt; } hr { border: 0; border-top: 1px solid #ccc; margin: 10pt 0; }`;

function documentHtml({ title, body, orientation = "portrait", word = false }) {
  const page = `@page { size: A4 ${orientation}; margin: ${orientation === "landscape" ? "1.5cm" : "2cm"}; }`;
  const head = word
    ? `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${esc(title)}</title>
       <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->`
    : `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>`;
  return `${head}<style>${page}${STYLE}</style></head><body>${title && !/^<h1/.test(body.trim()) ? `<h1>${esc(title)}</h1>` : ""}${body}</body></html>`;
}

export function saveWord({ filename, title, markdown, orientation }) {
  const html = documentHtml({ title, body: mdToHtml(markdown), orientation, word: true });
  saveAs(new Blob(["﻿", html], { type: "application/msword" }), `${safeName(filename)}.doc`);
}

// Хэвлэх цонх нээж "PDF болгож хадгалах" — кирилл үсэг, хүснэгт зөв гарна
export function printPdf({ title, markdown, orientation }) {
  const w = window.open("", "_blank");
  if (!w) throw new Error("Попап цонх хаагдсан байна — хөтчийн зөвшөөрлийг нээгээд дахин дарна уу");
  w.document.write(documentHtml({ title, body: mdToHtml(markdown), orientation }));
  w.document.close();
  w.onload = () => setTimeout(() => { w.focus(); w.print(); }, 200);
}

async function savePptx({ filename, title, slides }) {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const pres = new PptxGenJS();
  pres.layout = "LAYOUT_WIDE"; // 13.33 × 7.5 in
  pres.title = title || filename;
  const font = "Arial";
  if (title) {
    const s = pres.addSlide();
    s.background = { color: PURPLE };
    s.addText(plain(title), { x: 0.8, y: 2.6, w: 11.7, h: 1.6, fontFace: font, fontSize: 40, bold: true, color: "FFFFFF" });
  }
  for (const sl of slides || []) {
    const s = pres.addSlide();
    s.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: 13.33, h: 0.12, fill: { color: PURPLE }, line: { color: PURPLE } });
    s.addText(plain(sl.title), { x: 0.6, y: 0.35, w: 12.1, h: 0.9, fontFace: font, fontSize: 30, bold: true, color: PURPLE });
    const bullets = (sl.bullets || []).filter(Boolean);
    const table = Array.isArray(sl.table) && sl.table.length ? sl.table : null;
    let y = 1.4;
    if (bullets.length) {
      const h = table ? Math.min(2.6, 0.5 * bullets.length + 0.2) : 5.6;
      s.addText(bullets.map((b) => ({ text: plain(b), options: { bullet: true, paraSpaceAfter: 8 } })), { x: 0.7, y, w: 11.9, h, fontFace: font, fontSize: bullets.length > 6 ? 18 : 22, color: "1C1C1C", valign: "top" });
      y += h + 0.15;
    }
    if (table) {
      const rows = table.map((r, i) => (r || []).map((c) => ({ text: plain(c), options: i === 0 ? { bold: true, fill: { color: "F3EEFF" } } : {} })));
      s.addTable(rows, { x: 0.7, y, w: 11.9, fontFace: font, fontSize: 14, border: { type: "solid", pt: 0.75, color: "BBBBBB" }, autoPage: false });
    }
    if (sl.notes) s.addNotes(plain(sl.notes));
  }
  await pres.writeFile({ fileName: `${safeName(filename)}.pptx` });
}

function saveSheets({ filename, sheets, format }) {
  const list = (sheets || []).filter((s) => Array.isArray(s.rows));
  if (format === "csv") {
    const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(list[0]?.rows || []));
    return saveAs(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }), `${safeName(filename)}.csv`);
  }
  const wb = XLSX.utils.book_new();
  list.forEach((s, i) => XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s.rows), safeName(s.name || `Sheet${i + 1}`).slice(0, 31)));
  saveAs(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })]), `${safeName(filename)}.xlsx`);
}

// AI-ийн create_file тодорхойлолт → файл
export async function buildFile(d) {
  const f = d.format;
  if (f === "pptx") return savePptx(d);
  if (f === "xlsx" || f === "csv") return saveSheets(d);
  if (f === "docx") return saveWord(d);
  if (f === "pdf") return printPdf(d);
  const name = safeName(d.filename);
  if (f === "html") return saveAs(new Blob([documentHtml({ title: d.title, body: mdToHtml(d.markdown), orientation: d.orientation })], { type: "text/html;charset=utf-8" }), `${name}.html`);
  return saveAs(new Blob([d.markdown || ""], { type: "text/plain;charset=utf-8" }), `${name}.${f === "md" ? "md" : "txt"}`);
}

export const FORMAT_LABEL = { docx: "Word", pdf: "PDF", pptx: "PowerPoint", xlsx: "Excel", csv: "CSV", md: "Markdown", txt: "Текст", html: "HTML" };
